/**
 * Higher-level session helpers built on top of WbAgentClient.
 *
 * This module provides turn-level abstractions (step extraction, message
 * threading, parent resolution) while keeping zero React/UI dependencies so
 * it can be consumed by any frontend framework.
 */

import {
  TERMINAL_STATES,
  type TurnResponse,
  type TurnState,
  type WbAgentClient,
} from './WbAgentClient';

// =============================================================================
// Constants
// =============================================================================

export const STATE_LABELS: Record<TurnState, string> = {
  queued: 'Queued',
  in_progress: 'Running',
  completed: 'Completed',
  errored: 'Error',
  cancelled: 'Cancelled',
};

// =============================================================================
// Types
// =============================================================================

export type ProgressStep = {
  type: 'tool_call' | 'message';
  label: string;
  detail?: string;
  timestamp?: string;
};

export type ChatMessage =
  | {role: 'user'; text: string}
  | {role: 'agent'; text: string}
  | {role: 'steps'; steps: ProgressStep[]}
  | {role: 'loading'; steps: ProgressStep[]};

export type Tab = 'messages' | 'history';

// =============================================================================
// Step extraction
// =============================================================================

/** Best-effort one-liner from tool call arguments. */
export const summarizeToolArgs = (
  _name: string,
  args: Record<string, unknown>
): string | undefined => {
  for (const key of [
    'query',
    'input',
    'prompt',
    'expression',
    'filter',
    'path',
    'url',
    'name',
  ]) {
    const val = args[key];
    if (typeof val === 'string' && val.length > 0) {
      return val;
    }
  }
  for (const val of Object.values(args)) {
    if (typeof val === 'string' && val.length > 0) {
      return val;
    }
  }
  return undefined;
};

/** Extract progress steps from a turn's messages + tool_calls. */
export const extractSteps = (turn: TurnResponse): ProgressStep[] => {
  const steps: ProgressStep[] = [];
  const allMessages = turn.messages ?? [];
  for (let mi = 0; mi < allMessages.length; mi++) {
    const msg = allMessages[mi];
    const inner = msg.message as Record<string, unknown>;
    if (mi === 0 && inner.role === 'user') {
      continue;
    }
    const content = typeof inner.content === 'string' ? inner.content : null;
    if (content) {
      steps.push({type: 'message', label: content, timestamp: msg.timestamp});
    }
  }
  for (const tc of turn.tool_calls ?? []) {
    if (tc.type === 'invocation' && 'name' in tc) {
      const args = tc.arguments as Record<string, unknown>;
      steps.push({
        type: 'tool_call',
        label: tc.name,
        detail: summarizeToolArgs(tc.name, args),
        timestamp: tc.timestamp,
      });
    }
  }
  steps.sort((a, b) => (a.timestamp ?? '').localeCompare(b.timestamp ?? ''));
  return steps;
};

// =============================================================================
// Turn threading
// =============================================================================

/** Convert a list of ordered turns into ChatMessage entries. */
export function turnsToMessages(turns: TurnResponse[]): {
  messages: ChatMessage[];
  activeTurnId: string | null;
} {
  const messages: ChatMessage[] = [];
  let activeTurnId: string | null = null;
  for (const turn of turns) {
    messages.push({role: 'user', text: turn.user_prompt});
    const steps = extractSteps(turn);
    if (turn.final_output) {
      if (steps.length > 0) {
        messages.push({role: 'steps', steps});
      }
      messages.push({role: 'agent', text: turn.final_output});
    } else if (!TERMINAL_STATES.includes(turn.state)) {
      messages.push({role: 'loading', steps: []});
      activeTurnId = turn.id;
    } else if (turn.state === 'errored') {
      messages.push({role: 'agent', text: 'Error'});
    }
  }
  return {messages, activeTurnId};
}

/** Strip trailing cancelled/errored exchanges from a message list. */
export function stripFailedTail(messages: ChatMessage[]): ChatMessage[] {
  const trimmed = [...messages];
  while (trimmed.length > 0) {
    const last = trimmed[trimmed.length - 1];
    if (
      last.role === 'agent' &&
      (last.text === 'Cancelled.' || last.text === 'Error')
    ) {
      trimmed.pop();
      if (trimmed.length > 0 && trimmed[trimmed.length - 1].role === 'steps') {
        trimmed.pop();
      }
      if (trimmed.length > 0 && trimmed[trimmed.length - 1].role === 'user') {
        trimmed.pop();
      }
    } else {
      break;
    }
  }
  return trimmed;
}

/**
 * Find the best parent turn for a new message in a conversation.
 * Walks back from the leaf, skipping cancelled/errored turns so the new
 * turn effectively replaces the failed one.
 */
export async function getParentTurnId(
  client: WbAgentClient,
  rootTurnId: string
): Promise<string> {
  const turns = await client.get('/turns', {
    root_turn_id: rootTurnId,
    sort_by: 'created_at',
    sort_desc: false,
  });
  if (turns.length === 0) {
    return rootTurnId;
  }
  const parentIds = new Set(
    turns.map((t: TurnResponse) => t.parent_turn_id).filter(Boolean)
  );
  const leaves = turns.filter((t: TurnResponse) => !parentIds.has(t.id));
  let leaf =
    leaves.length > 0 ? leaves[leaves.length - 1] : turns[turns.length - 1];

  const turnById = new Map(turns.map((t: TurnResponse) => [t.id, t]));
  while (
    leaf &&
    (leaf.state === 'cancelled' || leaf.state === 'errored') &&
    leaf.parent_turn_id
  ) {
    const parent = turnById.get(leaf.parent_turn_id);
    if (!parent) {
      break;
    }
    leaf = parent;
  }

  return leaf.id;
}

/**
 * Find the root_turn_id for the most recently active conversation.
 * Uses the most recent turn (of any kind) and returns its root.
 */
export async function getMostRecentSessionId(
  client: WbAgentClient
): Promise<string | null> {
  const mostRecent = await client
    .get('/turns', {sort_by: 'created_at', sort_desc: true})
    .catch(() => [] as TurnResponse[]);
  return mostRecent.length > 0 ? mostRecent[0].root_turn_id : null;
}
